import { CircleAlertIcon, MonitorIcon } from "lucide-react";

import { Alert, AlertDescription } from "../ui/alert";
import { Button } from "../ui/button";

import type { DesktopPanelCopy } from "./desktopPanelState";

function desktopStartLabel(starting: boolean, startError: string | null): string {
  if (starting) return "Starting…";
  if (startError !== null) return "Try again";
  return "Start desktop";
}

/** The screen shown before a display exists. */
export function DesktopStartPane(props: {
  readonly copy: DesktopPanelCopy;
  readonly canStart: boolean;
  readonly starting: boolean;
  readonly startError: string | null;
  readonly onStart: () => void;
  readonly onOpenBrowser: () => void;
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-4 text-center">
        <div className="flex size-10 items-center justify-center rounded-[var(--control-radius)] border border-border bg-card text-muted-foreground">
          <MonitorIcon aria-hidden="true" className="size-5" />
        </div>

        <div className="flex flex-col items-center gap-1">
          <div className="flex items-center gap-2">
            {props.starting ? (
              <span
                aria-hidden
                data-testid="desktop-starting-indicator"
                className="size-1.5 shrink-0 rounded-full bg-info animate-status-pulse"
              />
            ) : null}
            <div className="text-sm font-medium text-foreground">{props.copy.title}</div>
          </div>
          <div className="max-w-[34ch] text-sm text-muted-foreground md:text-xs">
            {props.copy.detail}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            data-testid="desktop-start"
            disabled={!props.canStart}
            onClick={props.onStart}
            className="min-h-touch md:min-h-0"
          >
            {desktopStartLabel(props.starting, props.startError)}
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="desktop-open-browser"
            onClick={props.onOpenBrowser}
            className="min-h-touch md:min-h-0"
          >
            Open browser
          </Button>
        </div>

        {props.startError === null ? null : (
          <Alert variant="error" className="max-w-sm py-2 text-left">
            <CircleAlertIcon aria-hidden="true" />
            <AlertDescription>
              <p
                data-testid="desktop-start-error"
                className="break-words text-destructive-foreground"
              >
                {props.startError}
              </p>
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}
